import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Toaster, toast } from 'sonner';
import { User, Lock, Camera, Loader2, CheckCircle } from 'lucide-react';
import api from '../lib/api';
import AdminSEO from '../components/common/AdminSEO';

const StaffSetup = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { setUser, setToken } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [invite, setInvite] = useState(null);
  const [error, setError] = useState(null);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [photoUrl, setPhotoUrl] = useState('');
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  
  const token = searchParams.get('token');
  
  // Restore form state from sessionStorage on mount
  useEffect(() => {
    if (token) {
      const savedState = sessionStorage.getItem(`staff_setup_${token}`);
      if (savedState) {
        try {
          const { savedPhotoUrl } = JSON.parse(savedState);
          if (savedPhotoUrl) setPhotoUrl(savedPhotoUrl);
        } catch (e) {
          console.error('Failed to restore form state:', e);
        }
      }
    }
  }, [token]);
  
  // Save form state to sessionStorage when it changes
  useEffect(() => {
    if (token && photoUrl) {
      sessionStorage.setItem(`staff_setup_${token}`, JSON.stringify({
        savedPhotoUrl: photoUrl
      }));
    }
  }, [token, photoUrl]);

  useEffect(() => {
    if (!token) {
      setError('No invite token provided');
      setLoading(false);
      return;
    }
    
    fetchInviteDetails();
  }, [token]);

  const fetchInviteDetails = async () => {
    try {
      const res = await api.get(`/auth/staff/invite/${token}`);
      setInvite(res.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Invalid or expired invite link');
    } finally {
      setLoading(false);
    }
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Photo must be under 5MB');
      return;
    }
    
    // Validate image type
    if (!file.type.startsWith('image/')) {
      toast.error('Please upload an image file');
      return;
    }
    
    setUploadingPhoto(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('invite_token', token || '');
      
      // Use the profile-photo endpoint which doesn't require auth
      const res = await api.post('/site-media/upload/profile-photo', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      setPhotoUrl(res.data.url);
      toast.success('Photo uploaded!');
    } catch (err) {
      console.error('Photo upload error:', err);
      toast.error(err.response?.data?.detail || 'Failed to upload photo');
    } finally {
      setUploadingPhoto(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    
    if (password !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    
    setSubmitting(true);
    try {
      const res = await api.post('/auth/staff/setup', {
        token,
        password,
        photo_url: photoUrl || null
      });
      
      // Clear saved form state
      sessionStorage.removeItem(`staff_setup_${token}`);
      
      // Auto login
      setToken(res.data.token);
      setUser(res.data.user);
      localStorage.setItem('upa_token', res.data.token);
      localStorage.setItem('upa_user', JSON.stringify(res.data.user));
      
      toast.success('Account created successfully!');
      
      // Redirect based on role
      setTimeout(() => {
        if (res.data.user.role === 'teacher') {
          navigate('/teacher');
        } else {
          navigate('/admin');
        }
      }, 1500);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to set up account');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <AdminSEO title="Staff Setup" />
        <Loader2 className="w-8 h-8 text-[#FF007F] animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center px-4">
        <AdminSEO title="Invalid Invite" />
        <Card className="w-full max-w-md bg-[#111] border-red-500/30">
          <CardContent className="p-8 text-center">
            <div className="w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-3xl">😕</span>
            </div>
            <h2 className="text-xl font-bold text-white mb-2">Invalid Invite</h2>
            <p className="text-white/60 mb-6">{error}</p>
            <Button onClick={() => navigate('/login')} className="bg-[#FF007F] hover:bg-[#D6006A]">
              Go to Login
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505] flex items-center justify-center px-4 py-12">
      <AdminSEO title="Staff Setup" />
      <Toaster position="top-right" richColors />
      
      <Card className="w-full max-w-md bg-[#111] border-[#333]">
        <CardHeader className="text-center pb-2">
          <div className="mx-auto w-20 h-20 bg-gradient-to-br from-[#FF007F] to-[#7000FF] rounded-2xl flex items-center justify-center mb-4 shadow-lg">
            <span className="text-4xl">🎭</span>
          </div>
          <CardTitle className="text-2xl text-white">Welcome to UPA!</CardTitle>
          <CardDescription className="text-white/60">
            Set up your {invite?.role === 'teacher' ? 'Teacher' : 'Staff'} account
          </CardDescription>
        </CardHeader>
        
        <CardContent>
          <div className="mb-6 p-4 bg-[#1a1a1a] rounded-lg border border-[#333]">
            <p className="text-white font-medium">{invite?.first_name} {invite?.last_name}</p>
            <p className="text-white/50 text-sm">{invite?.email}</p>
            <span className={`inline-block mt-2 px-2 py-1 rounded text-xs font-medium ${
              invite?.role === 'teacher' 
                ? 'bg-purple-500/20 text-purple-400' 
                : 'bg-orange-500/20 text-orange-400'
            }`}>
              {invite?.role === 'teacher' ? 'Teacher' : 'Staff Member'}
            </span>
          </div>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Profile Photo */}
            <div className="space-y-2">
              <Label className="text-white/80">Profile Photo (Optional)</Label>
              <div className="flex items-center gap-4">
                <div className="w-20 h-20 rounded-full bg-[#1a1a1a] border-2 border-dashed border-[#333] flex items-center justify-center overflow-hidden">
                  {photoUrl ? (
                    <img src={photoUrl} alt="Profile" className="w-full h-full object-cover" />
                  ) : (
                    <Camera className="w-8 h-8 text-white/30" />
                  )}
                </div>
                <div>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handlePhotoUpload}
                    className="hidden"
                    id="photo-upload"
                    disabled={uploadingPhoto}
                  />
                  <label htmlFor="photo-upload">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      disabled={uploadingPhoto}
                      className="cursor-pointer border-[#333] text-white hover:bg-[#222]"
                      asChild
                    >
                      <span>
                        {uploadingPhoto ? (
                          <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Uploading...</>
                        ) : (
                          <><Camera className="w-4 h-4 mr-2" /> Upload Photo</>
                        )}
                      </span>
                    </Button>
                  </label>
                  {photoUrl && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => setPhotoUrl('')}
                      className="ml-2 text-red-400 hover:text-red-300"
                    >
                      Remove
                    </Button>
                  )}
                </div>
              </div>
            </div>
            
            {/* Password */}
            <div className="space-y-2">
              <Label htmlFor="password" className="text-white/80">Password *</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Min 6 characters"
                  required
                  className="pl-10 bg-[#1a1a1a] border-[#333] text-white"
                  data-testid="setup-password"
                />
              </div>
            </div>
            
            {/* Confirm Password */}
            <div className="space-y-2">
              <Label htmlFor="confirm-password" className="text-white/80">Confirm Password *</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
                <Input
                  id="confirm-password"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Confirm your password"
                  required
                  className="pl-10 bg-[#1a1a1a] border-[#333] text-white"
                  data-testid="setup-confirm-password"
                />
              </div>
              {password && confirmPassword && password === confirmPassword && (
                <p className="text-green-400 text-xs flex items-center gap-1">
                  <CheckCircle className="w-3 h-3" /> Passwords match
                </p>
              )}
            </div>
            
            <Button
              type="submit"
              disabled={submitting}
              className="w-full h-11 bg-[#FF007F] hover:bg-[#D6006A] text-white font-bold"
              data-testid="setup-submit"
            >
              {submitting ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Setting up...</>
              ) : (
                'Complete Setup'
              )}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default StaffSetup;
